// Login federado con Google / Apple vía el Hosted UI de Cognito (OAuth 2.0
// Authorization Code Grant + PKCE).
//
// CONVIVE con el login email+contraseña (ver `cognito.ts`, que llama directo a
// la API de Cognito con USER_PASSWORD/SRP). Aquí, en cambio, redirigimos al
// dominio del Hosted UI de Cognito indicando el `identity_provider`; Cognito
// lleva al usuario a Google/Apple, y al volver nos entrega un `code` en la
// `redirect_uri` (/auth/callback). Intercambiamos ese `code` por tokens en el
// endpoint `/oauth2/token` y los guardamos con el MISMO `setSession` del resto
// de la app, así el flujo posterior (permisos, tenant, refresh) es idéntico.
//
// PKCE (S256): el App client es público (sin secreto), así que usamos code
// verifier/challenge para proteger el intercambio del code. El `state` protege
// contra CSRF y transporta a dónde volver tras el login.

import { config } from '../config.js';
import type { CognitoTokens } from './cognito.js';

/** Proveedores de identidad soportados en el Hosted UI de Cognito. */
export type FederatedProvider = 'Google' | 'SignInWithApple';

/** Claves de `sessionStorage` para el estado efímero del flujo PKCE. */
const PKCE_VERIFIER_KEY = 'vinilos.oauth.pkce_verifier';
const STATE_KEY = 'vinilos.oauth.state';
const RETURN_TO_KEY = 'vinilos.oauth.return_to';

/** Base de los endpoints OAuth del Hosted UI (sin barra final). */
function hostedUiBase(): string {
  return `https://${config.cognitoDomain}`;
}

/** Genera una cadena aleatoria segura (base64url) de `bytes` bytes. */
function randomUrlSafe(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr.buffer);
}

/** Codifica un ArrayBuffer en base64url (sin relleno). */
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Deriva el code challenge (S256) a partir del verifier. */
async function deriveChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

/**
 * Inicia el login federado: construye la URL de autorización del Hosted UI con
 * PKCE y redirige el navegador. `returnTo` es la ruta de la app a la que volver
 * tras completar el login (por defecto, el catálogo).
 */
export async function startFederatedLogin(
  provider: FederatedProvider,
  returnTo = '/',
): Promise<void> {
  const verifier = randomUrlSafe(48);
  const challenge = await deriveChallenge(verifier);
  const state = randomUrlSafe(16);

  // Estado efímero del flujo: se valida/consume en el callback. `sessionStorage`
  // (no `localStorage`) porque es de un solo uso y por pestaña.
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.cognitoClientId,
    redirect_uri: config.oauthRedirectUri,
    identity_provider: provider,
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${hostedUiBase()}/oauth2/authorize?${params.toString()}`);
}

/** Resultado del intercambio del code: tokens + a dónde volver. */
export interface FederatedCallbackResult {
  tokens: CognitoTokens;
  returnTo: string;
}

/**
 * Procesa el callback OAuth: valida el `state`, intercambia el `code` por tokens
 * en `/oauth2/token` usando el code verifier (PKCE) y devuelve los tokens. Lanza
 * `Error` si falta/!coincide el estado o el intercambio falla. Limpia el estado
 * efímero en cualquier caso.
 */
export async function completeFederatedLogin(
  search: string,
): Promise<FederatedCallbackResult> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';

  // Limpieza del estado efímero (un solo uso), pase lo que pase.
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);

  if (error) {
    throw new Error(errorDescription || error);
  }
  if (!code) {
    throw new Error('Falta el código de autorización en la respuesta.');
  }
  if (!verifier || !expectedState || returnedState !== expectedState) {
    throw new Error('El estado de autenticación no coincide. Vuelve a intentarlo.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.cognitoClientId,
    code,
    redirect_uri: config.oauthRedirectUri,
    code_verifier: verifier,
  });
  const res = await fetch(`${hostedUiBase()}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  const json = (await res.json().catch(() => ({}))) as {
    id_token?: string;
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    error?: string;
    error_description?: string;
  };
  if (!res.ok || !json.id_token || !json.access_token) {
    throw new Error(json.error_description || json.error || 'No se pudo completar el inicio de sesión.');
  }

  return {
    tokens: {
      idToken: json.id_token,
      accessToken: json.access_token,
      refreshToken: json.refresh_token,
      expiresIn: json.expires_in ?? 3600,
    },
    returnTo,
  };
}
